import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, Save, X as XIcon,
  CalendarDays, MapPin, UserCog, FileText, Video, Users, ThumbsUp, ThumbsDown,
  ChevronDown, ChevronRight, Paperclip, Image as ImageIcon, FileSpreadsheet, File as FileIcon, Download, UploadCloud,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin, isTrainer } from "../contexts/AuthContext";

const ENVIRONMENT = ["Attitude/Energy", "Communication", "Body Language", "Focus/Engagement", "Competitiveness", "Work Rate", "Effort/Fight", "Resilience", "Coachability", "Accountability"];
const envKey = (t) => t.replace(/\W+/g, "-").toLowerCase();
const POS = { color: "#0a0a0a", background: "#4ade80", borderColor: "#4ade80" };
const NEG = { color: "#fff", background: "#dc2626", borderColor: "#dc2626" };

/** Today's date as YYYY-MM-DD (local time). */
function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d - off).toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

const EMPTY_DRAFT = {
  date: "",
  session_type: "on_court",
  location: "",
  trainer: "",
  environment: {},
  notes: "",
  attachments: [],
};

const fmtSize = (b) => (!b ? "" : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const attIcon = (ct = "") => ct.startsWith("image/") ? ImageIcon : ct.includes("sheet") || ct.includes("excel") || ct.includes("csv") ? FileSpreadsheet : ct.includes("pdf") || ct.includes("word") || ct.includes("text") ? FileText : FileIcon;

function AttachmentChip({ playerId, att, onRemove, testId }) {
  const Icon = attIcon(att.content_type);
  const isImg = (att.content_type || "").startsWith("image/");
  const url = api.workoutAttachmentUrl(playerId, att.id);
  return (
    <div className="group flex items-center gap-2 border border-white/10 bg-[#0f0f0f] pl-1.5 pr-2 py-1.5 max-w-full" data-testid={testId}>
      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 min-w-0" data-testid={`${testId}-open`}>
        {isImg ? (
          <img src={url} alt={att.name} className="w-8 h-8 object-cover border border-white/10 shrink-0" loading="lazy" />
        ) : (
          <span className="w-8 h-8 flex items-center justify-center bg-[#1c1c1c] border border-white/10 shrink-0"><Icon className="w-3.5 h-3.5 text-[var(--org-accent)]" /></span>
        )}
        <span className="min-w-0">
          <span className="block text-[12px] text-white truncate max-w-[180px] group-hover:text-[var(--org-accent)]">{att.name}</span>
          <span className="block text-[9px] text-[#757575] font-mono">{fmtSize(att.size)}</span>
        </span>
      </a>
      <a href={api.workoutAttachmentUrl(playerId, att.id, true)} className="p-1 text-[#757575] hover:text-white shrink-0" title="Download" data-testid={`${testId}-download`}><Download className="w-3 h-3" /></a>
      {onRemove && <button onClick={onRemove} className="p-1 text-[#757575] hover:text-red-400 shrink-0" title="Remove" data-testid={`${testId}-remove`}><XIcon className="w-3 h-3" /></button>}
    </div>
  );
}

function AttachmentUploader({ playerId, attachments, onChange }) {
  const [uploading, setUploading] = useState([]); // [{name, pct}]
  const inputRef = React.useRef(null);
  const onFiles = async (files) => {
    const list = Array.from(files || []).slice(0, 10 - attachments.length);
    if (list.length === 0) return;
    for (const f of list) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15 MB`); continue; }
      setUploading(u => [...u, { name: f.name, pct: 0 }]);
      try {
        const meta = await api.uploadWorkoutAttachment(playerId, f, (pct) => setUploading(u => u.map(x => x.name === f.name ? { ...x, pct } : x)));
        onChange([...attachments, meta]);
        attachments = [...attachments, meta]; // keep local ref current for multi-file loops
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`, { description: e?.response?.data?.detail || e.message });
      } finally {
        setUploading(u => u.filter(x => x.name !== f.name));
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments <span className="text-[#575757] normal-case tracking-normal">· photos, PDFs, docs · 15 MB max</span></div>
      {(attachments.length > 0 || uploading.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="workout-log-attachments">
          {attachments.map((a, i) => <AttachmentChip key={a.id} playerId={playerId} att={a} testId={`workout-log-att-${i}`} onRemove={() => onChange(attachments.filter(x => x.id !== a.id))} />)}
          {uploading.map((u) => (
            <div key={u.name} className="flex items-center gap-2 border border-dashed border-white/15 px-2.5 py-1.5 text-[11px] text-[#a0a0a0]" data-testid="workout-log-att-uploading">
              <Loader2 className="w-3 h-3 animate-spin text-[var(--org-accent)]" /> {u.name} <span className="font-mono text-[#575757]">{u.pct}%</span>
            </div>
          ))}
        </div>
      )}
      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="flex items-center justify-center gap-2 border border-dashed border-white/15 hover:border-[var(--org-accent)]/60 bg-[#0f0f0f] px-3 py-3 text-[11px] text-[#a0a0a0] hover:text-white cursor-pointer"
        data-testid="workout-log-att-dropzone"
      >
        <UploadCloud className="w-4 h-4 text-[var(--org-accent)]" /> Drop files here or <span className="underline">browse</span>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" data-testid="workout-log-att-input" />
      </label>
    </div>
  );
}

export default function PlayerWorkoutLog({ playerId, playerName }) {
  const { user } = useAuth();
  // Admins can edit any workout log. Trainers can edit workout logs on players they've been granted.
  // (Backend enforces the per-player grant; we optimistically enable the UI for any trainer.)
  const canEdit = isAdmin(user) || isTrainer(user);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editingId, setEditingId] = useState(null); // 'new' | log.id
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  // Collapse state — section defaults open, individual cards default collapsed (compact list).
  const [sectionOpen, setSectionOpen] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleCard = (id) => setExpandedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listWorkoutLogs(playerId);
      setLogs(r.logs || []);
      setTrainers(r.trainers || []);
      setLocations(r.locations || []);
    } catch { toast.error("Failed to load workout log"); }
    finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setDraft({ ...EMPTY_DRAFT, date: todayISO(), trainer: user?.name || "" });
    setEditingId("new");
    setSectionOpen(true); // ensure section is expanded when adding
  };

  const startEdit = (l) => {
    setDraft({
      date: l.date || todayISO(),
      session_type: l.session_type === "virtual" ? "virtual" : "on_court",
      location: l.location || "",
      trainer: l.trainer || "",
      environment: l.environment && typeof l.environment === "object" ? l.environment : {},
      notes: l.notes || "",
      attachments: Array.isArray(l.attachments) ? l.attachments : [],
    });
    setEditingId(l.id);
    setSectionOpen(true);
  };

  const cancel = () => { setEditingId(null); setDraft(EMPTY_DRAFT); };

  const setEnv = (trait, value) => {
    setDraft(d => {
      const env = { ...(d.environment || {}) };
      if (env[trait] === value) delete env[trait]; else env[trait] = value;
      return { ...d, environment: env };
    });
  };

  const save = async () => {
    if (!draft.date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try {
      const isVirtual = draft.session_type === "virtual";
      const payload = {
        date:         draft.date,
        session_type: isVirtual ? "virtual" : "on_court",
        // For virtual meetings we blank the on-court-only fields server-side too, so a session
        // toggled from on-court → virtual doesn't leave stale trainer/location/focus behind.
        location:     isVirtual ? "" : (draft.location || "").trim(),
        trainer:      isVirtual ? "" : (draft.trainer || "").trim(),
        environment:  isVirtual ? {} : (draft.environment || {}),
        notes:        (draft.notes || "").trim(),
        attachments:  draft.attachments || [],
      };
      if (editingId === "new") await api.createWorkoutLog(playerId, payload);
      else await api.updateWorkoutLog(playerId, editingId, payload);
      await load();
      cancel();
      toast.success(isVirtual ? "Virtual meeting logged" : "Workout logged");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const remove = async (l) => {
    const kind = l.session_type === "virtual" ? "virtual meeting" : "workout";
    if (!window.confirm(`Delete ${kind} from ${fmtDate(l.date)}?`)) return;
    try { await api.deleteWorkoutLog(playerId, l.id); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <section className="border border-white/10 bg-[#171717]" data-testid="workout-log">
      <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-white/10 flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setSectionOpen(v => !v)}
          className="flex items-center gap-3 text-left flex-1 min-w-0 group"
          data-testid="workout-log-section-toggle"
          aria-expanded={sectionOpen}
        >
          {sectionOpen
            ? <ChevronDown className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0" />
            : <ChevronRight className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0" />}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Workout &amp; Meeting Log</div>
            <div className="font-heading text-xl sm:text-2xl uppercase tracking-tight text-white truncate">
              {playerName || "Player"}'s Sessions
              {logs.length > 0 && (
                <span className="ml-2 text-[11px] font-normal tracking-[0.2em] text-[#757575] align-middle">· {logs.length}</span>
              )}
            </div>
          </div>
        </button>
        {canEdit && editingId !== "new" && (
          <button
            onClick={startNew}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold border bg-[var(--org-accent)] text-[#121212] border-transparent hover:opacity-90"
            data-testid="workout-log-add-btn"
          >
            <Plus className="w-3 h-3" /> New Session
          </button>
        )}
      </div>

      {sectionOpen && (
      <div className="p-4 sm:p-5 space-y-3">
        {editingId === "new" && (
          <LogEditor playerId={playerId}
            draft={draft}
            setDraft={setDraft}
            setEnv={setEnv}
            trainers={trainers}
            locations={locations}
            onSave={save}
            onCancel={cancel}
            saving={saving}
          />
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : logs.length === 0 && editingId !== "new" ? (
          <div className="border border-dashed border-white/10 px-4 py-6 text-center text-[12px] italic text-[#575757]">
            No sessions logged yet.{canEdit ? " Click New Session to log an on-court workout or virtual meeting." : ""}
          </div>
        ) : (
          logs.map(l => editingId === l.id ? (
            <LogEditor playerId={playerId}
              key={l.id}
              draft={draft}
              setDraft={setDraft}
              setEnv={setEnv}
              trainers={trainers}
              locations={locations}
              onSave={save}
              onCancel={cancel}
              saving={saving}
            />
          ) : (
            <LogCard playerId={playerId}
              key={l.id}
              l={l}
              canEdit={canEdit}
              expanded={expandedIds.has(l.id)}
              onToggle={() => toggleCard(l.id)}
              onEdit={() => startEdit(l)}
              onDelete={() => remove(l)}
            />
          ))
        )}
      </div>
      )}
    </section>
  );
}

function LogCard({ l, playerId, canEdit, expanded, onToggle, onEdit, onDelete }) {
  const isVirtual = l.session_type === "virtual";
  const env = Object.entries(l.environment || {});
  const hasAtts = Array.isArray(l.attachments) && l.attachments.length > 0;
  const hasBody = Boolean(l.notes) || hasAtts;
  return (
    <div className="border border-white/10 bg-[#161616]" data-testid={`workout-log-${l.id}`} data-session-type={l.session_type || "on_court"}>
      <div className={`flex items-start justify-between gap-3 px-4 py-3 ${expanded && hasBody ? "border-b border-white/5" : ""}`}>
        <button
          type="button"
          onClick={onToggle}
          className="min-w-0 flex-1 text-left flex items-start gap-2 group"
          aria-expanded={expanded}
          data-testid={`workout-log-toggle-${l.id}`}
        >
          {hasBody && (expanded
            ? <ChevronDown className="w-4 h-4 text-[#757575] group-hover:text-white mt-0.5 shrink-0" />
            : <ChevronRight className="w-4 h-4 text-[#757575] group-hover:text-white mt-0.5 shrink-0" />)}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {isVirtual ? <Video className="w-3.5 h-3.5 text-[#8ec5ff] shrink-0" /> : <CalendarDays className="w-3.5 h-3.5 text-[var(--org-accent)] shrink-0" />}
              <div className="font-heading text-base uppercase tracking-tight text-white">{fmtDate(l.date)}</div>
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] font-bold shrink-0"
                style={isVirtual
                  ? { color: "#121212", background: "#8ec5ff" }
                  : { color: "#121212", background: "var(--org-accent)" }}
              >
                {isVirtual ? <><Video className="w-2.5 h-2.5" /> Virtual</> : <><Users className="w-2.5 h-2.5" /> On Court</>}
              </span>
              {!isVirtual && l.location && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                  <MapPin className="w-3 h-3" /> {l.location}
                </span>
              )}
              {!isVirtual && l.trainer && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                  <UserCog className="w-3 h-3" /> {l.trainer}
                </span>
              )}
              {hasAtts && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#3aa0ff]" data-testid={`workout-log-att-badge-${l.id}`}>
                  <Paperclip className="w-3 h-3" /> {l.attachments.length} {l.attachments.length === 1 ? "file" : "files"}
                </span>
              )}
            </div>
            {!isVirtual && env.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`workout-log-env-${l.id}`}>
                {env.map(([t, v]) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold"
                    style={v === "positive" ? POS : NEG}
                    data-testid={`workout-log-env-chip-${envKey(t)}`}
                  >
                    {v === "positive" ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />} {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </button>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onEdit} className="p-1.5 text-[#a0a0a0] hover:text-white" data-testid={`workout-log-edit-${l.id}`}>
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={onDelete} className="p-1.5 text-[#a0a0a0] hover:text-red-400" data-testid={`workout-log-delete-${l.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Collapsible body: notes + attachments */}
      {expanded && hasBody && (
        <>
          {l.notes && (
            <div className="px-4 py-3">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-1 flex items-center gap-1">
                <FileText className="w-3 h-3" /> {isVirtual ? "Meeting Notes" : "Trainer Notes"}
              </div>
              <div className="text-[13px] text-[#c0c0c0] whitespace-pre-wrap break-words">{l.notes}</div>
            </div>
          )}
          {hasAtts && (
            <div className="px-4 pb-3 pt-1">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments ({l.attachments.length})</div>
              <div className="flex flex-wrap gap-2" data-testid={`workout-log-card-atts-${l.id}`}>
                {l.attachments.map((a, i) => <AttachmentChip key={a.id} playerId={playerId} att={a} testId={`workout-log-card-att-${i}`} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LogEditor({ draft, setDraft, setEnv, trainers, locations, onSave, onCancel, saving, playerId }) {
  const isVirtual = draft.session_type === "virtual";
  const env = draft.environment || {};
  const envCount = Object.keys(env).length;
  const [envOpen, setEnvOpen] = useState(true);
  const listIdTrainer = useMemo(() => `wl-trainers-${Math.random().toString(36).slice(2, 8)}`, []);
  const listIdLoc = useMemo(() => `wl-locations-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <div className="border-2 bg-[#161616]" style={{ borderColor: "var(--org-accent)66" }} data-testid="workout-log-editor">
      {/* Session-type toggle */}
      <div className="px-4 pt-3 pb-3 border-b border-white/10" style={{ background: "var(--org-accent)" }}>
        <div className="text-[9px] uppercase tracking-[0.3em] text-[#121212] font-bold mb-1.5">Session Type</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setDraft(d => ({ ...d, session_type: "on_court" }))}
            className={`flex items-center gap-2 px-3 py-2 border text-left ${!isVirtual ? "border-[#0a0a0a] bg-[var(--org-accent)] shadow-inner" : "border-white/10 bg-[#0f0f0f] hover:border-white/30"}`}
            data-testid="workout-log-type-on-court"
          >
            <Users className={`w-4 h-4 ${!isVirtual ? "text-[#121212]" : "text-[#a0a0a0]"}`} />
            <div>
              <div className={`text-[11px] uppercase tracking-[0.25em] font-bold ${!isVirtual ? "text-[#121212]" : "text-white"}`}>On Court</div>
              <div className={`text-[10px] ${!isVirtual ? "text-[#121212]/70" : "text-[#a0a0a0]"}`}>In-person workout · location, trainer, environment</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setDraft(d => ({ ...d, session_type: "virtual" }))}
            className={`flex items-center gap-2 px-3 py-2 border text-left ${isVirtual ? "border-[#0a0a0a] bg-[#8ec5ff]" : "border-white/10 bg-[#0f0f0f] hover:border-white/30"}`}
            data-testid="workout-log-type-virtual"
          >
            <Video className={`w-4 h-4 ${isVirtual ? "text-[#121212]" : "text-[#a0a0a0]"}`} />
            <div>
              <div className={`text-[11px] uppercase tracking-[0.25em] font-bold ${isVirtual ? "text-[#121212]" : "text-white"}`}>Virtual</div>
              <div className={`text-[10px] ${isVirtual ? "text-[#121212]/70" : "text-[#a0a0a0]"}`}>Zoom / call · date + feedback notes only</div>
            </div>
          </button>
        </div>
      </div>

      {/* Date + (on-court only: location + trainer) */}
      <div className={`px-4 py-3 border-b border-white/10 grid grid-cols-1 gap-3 ${isVirtual ? "" : "sm:grid-cols-3"}`}>
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[var(--org-accent)] mb-1">Date</div>
          <input
            type="date"
            value={draft.date}
            onChange={(e) => setDraft(d => ({ ...d, date: e.target.value }))}
            className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white"
            data-testid="workout-log-date"
          />
        </label>
        {!isVirtual && (
          <>
            <label className="block">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[var(--org-accent)] mb-1">Location</div>
              <input
                list={listIdLoc}
                value={draft.location}
                onChange={(e) => setDraft(d => ({ ...d, location: e.target.value }))}
                placeholder="Gym / facility"
                className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white"
                data-testid="workout-log-location"
              />
              <datalist id={listIdLoc}>
                {locations.map(x => <option key={x} value={x} />)}
              </datalist>
            </label>
            <label className="block">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[var(--org-accent)] mb-1">Trainer</div>
              <input
                list={listIdTrainer}
                value={draft.trainer}
                onChange={(e) => setDraft(d => ({ ...d, trainer: e.target.value }))}
                placeholder="Trainer name"
                className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white"
                data-testid="workout-log-trainer"
              />
              <datalist id={listIdTrainer}>
                {trainers.map(x => <option key={x} value={x} />)}
              </datalist>
            </label>
          </>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Environment read — on-court only: each trait is a positive / negative for the session */}
        {!isVirtual && (
          <div data-testid="workout-log-environment">
            <button
              type="button"
              onClick={() => setEnvOpen(v => !v)}
              className="w-full flex items-center justify-between mb-2 group"
              aria-expanded={envOpen}
              data-testid="workout-log-environment-toggle"
            >
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] group-hover:text-white">
                {envOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Environment
                {envCount > 0 && <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold tracking-normal" style={{ background: "var(--org-accent)", color: "#121212" }} data-testid="workout-log-environment-count">{envCount}</span>}
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#575757]">{envOpen ? <>Tap <ThumbsUp className="w-3 h-3 inline" /> positive or <ThumbsDown className="w-3 h-3 inline" /> negative · tap again to clear</> : "Expand to rate"}</div>
            </button>
            {envOpen && <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {ENVIRONMENT.map(trait => {
                const v = env[trait];
                return (
                  <div key={trait} className="flex items-stretch border" style={{ borderColor: v === "positive" ? "#4ade80" : v === "negative" ? "#dc2626" : "#333", background: "#0f0f0f" }} data-testid={`workout-log-env-${envKey(trait)}`}>
                    <div className={`flex-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold self-center ${v ? "text-white" : "text-[#c0c0c0]"}`}>{trait}</div>
                    <button
                      type="button"
                      onClick={() => setEnv(trait, "positive")}
                      className="px-2.5 border-l border-white/10 hover:text-[#4ade80]"
                      style={v === "positive" ? POS : { color: "#757575" }}
                      aria-pressed={v === "positive"}
                      title="Positive"
                      data-testid={`workout-log-env-${envKey(trait)}-positive`}
                    ><ThumbsUp className="w-3.5 h-3.5" /></button>
                    <button
                      type="button"
                      onClick={() => setEnv(trait, "negative")}
                      className="px-2.5 border-l border-white/10 hover:text-[#f87171]"
                      style={v === "negative" ? NEG : { color: "#757575" }}
                      aria-pressed={v === "negative"}
                      title="Negative"
                      data-testid={`workout-log-env-${envKey(trait)}-negative`}
                    ><ThumbsDown className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
            </div>}
          </div>
        )}

        {/* Notes — always visible */}
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1">
            {isVirtual ? "Meeting Notes" : "Trainer Notes"}
          </div>
          <textarea
            rows={3}
            value={draft.notes}
            onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
            placeholder={isVirtual
              ? "What was discussed, goals for next call, action items..."
              : "What went well, what to work on next time, effort/energy, cues that clicked..."}
            className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid="workout-log-notes"
          />
        </label>

        <AttachmentUploader playerId={playerId} attachments={draft.attachments || []} onChange={(atts) => setDraft(d => ({ ...d, attachments: atts }))} />

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#a0a0a0] hover:text-white"
            data-testid="workout-log-cancel-btn"
          >
            <XIcon className="w-3 h-3 inline mr-0.5" /> Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#121212] disabled:opacity-50"
            style={{ background: "var(--org-accent)" }}
            data-testid="workout-log-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} {isVirtual ? "Save Meeting" : "Save Workout"}
          </button>
        </div>
      </div>
    </div>
  );
}
